/**
 * The how-to loop every converted game shows before START (Marek, 6 Oct 2026: "all games need
 * pre-view animations of how to play"): the board's own `View`, played from a script of view
 * states, with the same ghost finger and the same numbered steps for every game. The board in the
 * demo is the board the guest is about to play, in tonight's era.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Lang } from '../../../ui/text.js';
import type { Era } from '../../art.js';
import type { DemoScript, Ease, FingerKey, ViewComponent } from './types.js';
import { FrameOverlay } from './frame.js';
import { INK, alpha, sans } from './tokens.js';

const EASE: Record<Ease, (f: number) => number> = {
  linear: (f) => f,
  inOut: (f) => f * f * (3 - 2 * f),
  in: (f) => f * f,
  out: (f) => 1 - (1 - f) * (1 - f),
  step: (f) => (f >= 1 ? 1 : 0),
};

/**
 * Between two values at `f` (0–1): numbers glide, lists of the same length and objects go part by
 * part, and anything else (a word, a switch, a list that grows) changes at the later key.
 */
export function mix<T>(a: T, b: T, f: number): T {
  if (f <= 0) return a;
  if (f >= 1) return b;
  if (typeof a === 'number' && typeof b === 'number') return (a + (b - a) * f) as T;
  if (Array.isArray(a) && Array.isArray(b)) {
    const to = b as unknown[];
    return (a.length === to.length ? (a as unknown[]).map((v, i) => mix(v, to[i], f)) : a) as T;
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const out: Record<string, unknown> = { ...(a as Record<string, unknown>) };
    for (const [k, v] of Object.entries(b)) out[k] = k in out ? mix(out[k], v, f) : v;
    return out as T;
  }
  return a;
}

/** The whole view at each key: every key's change laid over the one before. */
function fullKeys<V>(script: DemoScript<V>): { at: number; view: V; ease: Ease }[] {
  let view = {} as V;
  return script.keys.map((k) => {
    view = { ...view, ...k.view };
    return { at: k.at, view, ease: k.ease ?? 'linear' };
  });
}

/** The board `t` ms into the loop. Before the first key, the first; after the last, the last. */
export function viewAt<V>(script: DemoScript<V>, t: number): V {
  const keys = fullKeys(script);
  if (keys.length === 0) return {} as V;
  let i = 0;
  while (i + 1 < keys.length && keys[i + 1]!.at <= t) i++;
  const a = keys[i]!;
  const b = keys[i + 1];
  if (!b || t <= a.at) return a.view;
  return mix(a.view, b.view, EASE[b.ease]((t - a.at) / Math.max(1, b.at - a.at)));
}

export interface FingerNow {
  x: number;
  y: number;
  /** Pressing now. */
  down: boolean;
  /** 0 hidden, 1 on the board. */
  shown: number;
  /** ms since it last went down (for the ripple), or null. */
  pressedFor: number | null;
  /** Where it has been in the last moments while pressing: a drag's trail, oldest first. */
  trail: { x: number; y: number }[];
}

/** Down and hidden hold from their key until a key says otherwise. */
function fingerState(keys: readonly FingerKey[], t: number): { x: number; y: number; down: boolean; hide: boolean; downAt: number | null } {
  let down = false;
  let hide = false;
  let downAt: number | null = null;
  let i = -1;
  for (let k = 0; k < keys.length && keys[k]!.at <= t; k++) {
    const key = keys[k]!;
    if (key.down !== undefined) {
      if (key.down && !down) downAt = key.at;
      down = key.down;
    }
    if (key.hide !== undefined) hide = key.hide;
    i = k;
  }
  const a = keys[Math.max(0, i)];
  const b = keys[i + 1];
  if (!a) return { x: 160, y: 260, down: false, hide: true, downAt: null };
  if (i < 0 || !b) return { x: a.x, y: a.y, down, hide, downAt };
  const f = EASE[b.ease ?? 'inOut']((t - a.at) / Math.max(1, b.at - a.at));
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, down, hide, downAt };
}

/** The finger fades in or out over this long when it comes onto the board or leaves it. */
const FADE_MS = 250;

/** The ghost finger `t` ms into the loop. */
export function fingerAt(script: DemoScript<unknown>, t: number): FingerNow {
  const now = fingerState(script.finger, t);
  // It fades in or out from the moment it is shown or hidden (not at the start of the loop).
  let hidden: boolean | null = null;
  let changedAt: number | null = null;
  for (const k of script.finger) {
    if (k.at > t) break;
    if (k.hide === undefined) continue;
    if (hidden !== null && k.hide !== hidden) changedAt = k.at;
    hidden = k.hide;
  }
  const f = changedAt === null ? 1 : Math.min(1, (t - changedAt) / FADE_MS);
  const shown = now.hide ? 1 - f : f;
  const trail: { x: number; y: number }[] = [];
  if (now.down) {
    for (const back of [320, 260, 200, 140, 80]) {
      const then = fingerState(script.finger, t - back);
      if (then.down && t - back >= (now.downAt ?? t)) trail.push({ x: then.x, y: then.y });
    }
  }
  return { x: now.x, y: now.y, down: now.down, shown: Math.max(0, Math.min(1, shown)), pressedFor: now.downAt === null ? null : t - now.downAt, trail };
}

/** Which numbered step is showing `t` ms into the loop. */
export function stepAt(script: DemoScript<unknown>, t: number): number {
  let at = 0;
  script.steps.forEach((s, i) => {
    if (s.at <= t) at = i;
  });
  return at;
}

/**
 * The ghost finger, in board units: a fingertip from below right, its tip on (x, y). It dips when
 * it presses, with one ring opening from the tip; a held press keeps a soft ring; a drag leaves a
 * short fading trail. Nothing flashes.
 */
export function GhostFinger({ finger, accent }: { finger: FingerNow; accent: string }) {
  if (finger.shown <= 0) return null;
  const { x, y, down, pressedFor, trail } = finger;
  const ripple = pressedFor !== null && pressedFor < 650 ? pressedFor / 650 : null;
  return (
    <g opacity={finger.shown} pointerEvents="none">
      {trail.length > 1 && (
        <polyline
          points={[...trail, { x, y }].map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
          fill="none"
          stroke={INK.hi}
          strokeOpacity=".35"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {down && <circle cx={x} cy={y} r="15" fill={alpha(accent, 0.25)} stroke={INK.hi} strokeOpacity=".8" strokeWidth="2" />}
      {ripple !== null && <circle cx={x} cy={y} r={10 + ripple * 26} fill="none" stroke={INK.hi} strokeWidth="2.5" opacity={0.9 * (1 - ripple)} />}
      <g transform={`translate(${x} ${y}) rotate(-28) scale(${down ? 0.93 : 1})`}>
        {/* its shadow, then the finger: a rounded tip and a nail, pale, with a dark edge to read on any board */}
        <path d="M-11 6 C -11 -4 -6 -10 0 -10 C 6 -10 11 -4 11 6 L 13 92 L -13 92 Z" transform="translate(4 6)" fill="#000" opacity=".35" />
        <path d="M-11 6 C -11 -4 -6 -10 0 -10 C 6 -10 11 -4 11 6 L 13 92 L -13 92 Z" fill="#f4f1ee" fillOpacity=".93" stroke="#0b1220" strokeOpacity=".7" strokeWidth="1.5" />
        <path d="M-6 -2 C -6 -6 -3 -8 0 -8 C 3 -8 6 -6 6 -2 L 6 9 C 2 11 -2 11 -6 9 Z" fill="#ffffff" stroke="#c9b9ad" strokeWidth="1" />
        <path d="M-9 40 C -3 37 3 37 9 40" fill="none" stroke="#b8a89c" strokeWidth="1.2" opacity=".7" />
      </g>
    </g>
  );
}

/** The numbered steps under a demo, the one showing now lit. The same for every game. */
export function DemoSteps({ steps, at, lang, accent }: { steps: DemoScript<unknown>['steps']; at: number; lang: Lang; accent: string }) {
  return (
    <ol className="cc-demo-steps" style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {steps.map((s, i) => {
        const on = i === at;
        return (
          <li
            key={i}
            className={on ? 'on' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: sans,
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: 1,
              lineHeight: 1.15,
              color: on ? INK.hi : INK.faint,
              transition: 'color .25s',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: 22,
                height: 22,
                borderRadius: 11,
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                background: on ? accent : 'transparent',
                color: on ? INK.onFill : INK.faint,
                border: `1.5px solid ${on ? accent : INK.faint}`,
                transition: 'background .25s, color .25s, border-color .25s',
              }}
            >
              {i + 1}
            </span>
            <span>{lang === 'no' ? s.no : s.en}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** The loop's clock: ms into the loop, redrawn every frame. */
function useLoop(ms: number): number {
  const [t, setT] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let id = requestAnimationFrame(function frame() {
      setT((performance.now() - start) % ms);
      id = requestAnimationFrame(frame);
    });
    return () => cancelAnimationFrame(id);
  }, [ms]);
  return t;
}

/**
 * A game's how-to loop: its own board, played from `script`, with the ghost finger over it and
 * the numbered steps under it. `t` in the views is the loop's own time.
 */
export function DemoPlayer({
  View,
  script,
  era,
  accent,
  lang,
  at,
  boardStyle,
}: {
  /** The module's `View` and `demo` (any game's: they are checked against each other by `thirtyGame`). */
  View: ViewComponent<never>;
  script: DemoScript<unknown>;
  era: Era;
  accent: string;
  lang: Lang;
  /** A still at this moment of the loop, in place of the loop itself (tests, pictures). */
  at?: number | undefined;
  /** The board's box (its width, on a short screen); the steps take the full width under it. */
  boardStyle?: CSSProperties | undefined;
}) {
  const running = useLoop(script.ms);
  const t = at ?? running;
  const view = { ...(viewAt(script, t) as object), t: Math.round(t) };
  return (
    <div>
      <div style={boardStyle}>
        <FrameOverlay.Provider value={<GhostFinger finger={fingerAt(script, t)} accent={accent} />}>
          <View view={view as never} era={era} accent={accent} lang={lang} />
        </FrameOverlay.Provider>
      </div>
      <DemoSteps steps={script.steps} at={stepAt(script, t)} lang={lang} accent={accent} />
    </div>
  );
}
